import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { SearchIcon, XIcon } from "@heroicons/react/outline";
import { TruncatedText } from "../TruncatedText/TruncatedText";
import InfoIconWithTooltip from "../Icon/InfoIconWithTooltip";

interface DropdownOption {
  id: string | number;
  name: string;
  infoTooltip?: string;
}

export interface SearchableDropdownOptionGroup {
  label: string;
  options: DropdownOption[];
}

interface SearchableDropdownProps {
  options: DropdownOption[];
  optionGroups?: SearchableDropdownOptionGroup[];
  selectedId?: string | number;
  onSelect: (selectedId: string | number) => void;
  placeholder?: string;
  searchPlaceholder: string;
  noItemFound: React.ReactNode;
  listMaxHeightClassName?: string;
  filterSlot?: React.ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  placement?: "bottom" | "top";
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  optionGroups,
  selectedId,
  onSelect,
  placeholder,
  searchPlaceholder,
  noItemFound,
  listMaxHeightClassName = "max-h-48",
  filterSlot,
  onOpenChange,
  placement = "bottom",
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const matchesSearch = (option: DropdownOption) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase());

  const allOptions = optionGroups ? optionGroups.flatMap((g) => g.options) : options;
  const filteredOptions = allOptions.filter(matchesSearch);
  const filteredGroups = optionGroups
    ?.map((group) => ({ ...group, options: group.options.filter(matchesSearch) }))
    .filter((group) => group.options.length > 0);

  const getDropdownStyles = () => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    if (placement === "top") {
      return {
        top: rect.top + window.scrollY - 6,
        left: rect.left + window.scrollX,
        width: rect.width,
        transform: "translateY(-100%)",
      };
    }
    return {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    };
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
      setSearchTerm("");
    }
    onOpenChange?.(isOpen);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  const selectedOption = allOptions.find((option) => String(option.id) === String(selectedId));

  const clearSearch = () => {
    setSearchTerm("");
    searchInputRef.current?.focus();
  };

  const renderOption = (option: DropdownOption) => (
    <li
      key={option.id}
      onClick={() => {
        onSelect(option.id);
        setIsOpen(false);
      }}
      className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm"
    >
      <TruncatedText text={option?.name} />
      {option.infoTooltip && <InfoIconWithTooltip message={option.infoTooltip} />}
    </li>
  );

  const DropdownList = (
    <div
      ref={dropdownRef}
      style={getDropdownStyles()}
      className="absolute border border-gray-300 rounded-lg bg-white shadow-lg z-50 overflow-hidden"
    >
      <div className="relative border-b border-gray-200">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-5 w-5 text-gray-400" strokeWidth={2} />
        </div>
        <input
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full pl-10 pr-8 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-inset"
        />
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            type="button"
            aria-label="Clear"
          >
            <XIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" strokeWidth={2} />
          </button>
        )}
      </div>
      {filterSlot && <div className="px-3 py-2 border-b border-gray-200">{filterSlot}</div>}
      <ul className={`overflow-y-auto ${listMaxHeightClassName}`}>
        {filteredOptions.length > 0 ? (
          filteredGroups ? (
            filteredGroups.map((group) => (
              <React.Fragment key={group.label}>
                <li className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                  {group.label}
                </li>
                {group.options.map((option) => renderOption(option))}
              </React.Fragment>
            ))
          ) : (
            filteredOptions.map((option) => renderOption(option))
          )
        ) : (
          <li className="px-3 py-2 text-gray-500 text-sm">{noItemFound}</li>
        )}
      </ul>
    </div>
  );

  return (
    <div className="relative w-full">
      <div
        ref={triggerRef}
        className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white cursor-pointer flex justify-between items-center w-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;charset=utf-8,<svg width='12' height='12' fill='none' xmlns='http:%2F%2Fwww.w3.org/2000/svg'><path d='M1 1l4 4 4-4' stroke='rgb(0, 200, 137)' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
          backgroundPosition: "right 0.5rem top 1rem",
          backgroundSize: "1.5em 1.5em",
          backgroundRepeat: "no-repeat",
        }}
      >
        <span className="truncate">
          {selectedOption ? selectedOption.name : placeholder || t("general.selectAnItem")}
        </span>
      </div>
      {isOpen && createPortal(DropdownList, document.body)}
    </div>
  );
};

export default SearchableDropdown;
